'use client'

import { useEffect, useState, useCallback } from 'react'
import { ConnectionError } from '@/lib/types/config'
import { ConnectionErrorOverlay } from '@/components/errors/ConnectionErrorOverlay'
import { getConfig, resetConfig } from '@/lib/config'
import { useI18n } from '@/lib/hooks/use-i18n'
import { runtimeTarget } from '@/lib/runtime-target'

interface ConnectionGuardProps {
  children: React.ReactNode
}

const DESKTOP_STARTUP_RETRY_COUNT = 30
const DESKTOP_STARTUP_RETRY_DELAY_MS = 2000

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms))
}

export function ConnectionGuard({ children }: ConnectionGuardProps) {
  const [error, setError] = useState<ConnectionError | null>(null)
  const [isChecking, setIsChecking] = useState(true)
  const { t } = useI18n()

  const loadConfig = useCallback(async () => {
    const config = await getConfig()

    // Check if database is offline
    if (config.dbStatus === 'offline') {
      setError({
        type: 'database-offline',
        details: {
          message: t('connection.db.offline.subtitle'),
          attemptedUrl: config.apiUrl,
        },
      })
      setIsChecking(false)
      return false
    }

    // If we got here, connection is good
    setError(null)
    setIsChecking(false)
    return true
  }, [t])

  const checkConnection = useCallback(async () => {
    setIsChecking(true)
    setError(null)

    // Reset config cache to force a fresh fetch
    resetConfig()

    try {
      await loadConfig()
      return
    } catch (err) {
      if (runtimeTarget === 'desktop') {
        for (let attempt = 0; attempt < DESKTOP_STARTUP_RETRY_COUNT; attempt += 1) {
          await sleep(DESKTOP_STARTUP_RETRY_DELAY_MS)
          resetConfig()

          try {
            const connected = await loadConfig()
            if (connected) {
              return
            }
          } catch {
            // Keep retrying during desktop runtime startup.
          }
        }
      }

      // API is unreachable
      const errorMessage =
        err instanceof Error ? err.message : t('connection.error.unknown')
      const attemptedUrl =
        typeof window !== 'undefined'
          ? `${window.location.origin}/agent-api/config`
          : undefined

      setError({
        type: 'api-unreachable',
        details: {
          message: t('connection.api.unreachable.subtitle'),
          technicalMessage: errorMessage,
          stack: err instanceof Error ? err.stack : undefined,
          attemptedUrl,
        },
      })
      setIsChecking(false)
    }
  }, [loadConfig, t])

  // Check connection on mount
  useEffect(() => {
    checkConnection()
  }, [checkConnection])

  // Add keyboard shortcut for retry (R key)
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (error && (e.key === 'r' || e.key === 'R')) {
        e.preventDefault()
        checkConnection()
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [error, checkConnection])

  // Show overlay if there's an error
  if (error) {
    return <ConnectionErrorOverlay error={error} onRetry={checkConnection} />
  }

  // Show nothing while checking (prevents flash of content)
  if (isChecking) {
    return null
  }

  // Render children if connection is good
  return <>{children}</>
}
